/* CHOICE-style vertical product gallery + fullscreen zoom viewer. Powers <lux-gallery>. */
if (!customElements.get('lux-gallery')) {
  customElements.define(
    'lux-gallery',
    class LuxGallery extends HTMLElement {
      connectedCallback() {
        this.stage = this.querySelector('.lux-gallery__stage');
        this.track = this.querySelector('.lux-gallery__track');
        this.slides = Array.from(this.querySelectorAll('.lux-gallery__slide'));
        this.thumbs = Array.from(this.querySelectorAll('.lux-gallery__thumb'));
        this.dots = Array.from(this.querySelectorAll('.lux-gallery__dot'));
        this.count = this.slides.length;
        this.index = 0;

        // Fullscreen zoom viewer works for any image count (even a single image).
        this.buildZoom();
        this.bindZoomOpen();

        if (this.count <= 1) return;

        this.bind();
        this.render(false);

        // Recompute pixel offset when the stage resizes.
        this._onResize = () => this.render(false);
        window.addEventListener('resize', this._onResize, { passive: true });
      }

      disconnectedCallback() {
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        if (this._onKey) document.removeEventListener('keydown', this._onKey);
        // Remove the variant listeners too, or each re-render (theme editor, apps
        // that re-render the media gallery) leaks a stale document/section listener.
        if (this._variantHandler) {
          if (this._variantInfoEl) this._variantInfoEl.removeEventListener('variant:change', this._variantHandler);
          document.removeEventListener('on:variant:change', this._variantHandler);
        }
        // The lightbox was relocated to <body>; take it with us on teardown.
        if (this.lightbox && this.lightbox.parentNode) this.lightbox.parentNode.removeChild(this.lightbox);
        document.body.classList.remove('lux-zoom-open');
      }

      clamp(i) {
        return Math.max(0, Math.min(this.count - 1, i));
      }

      bind() {
        const prev = this.querySelector('.lux-gallery__nav--prev');
        const next = this.querySelector('.lux-gallery__nav--next');
        if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1));
        if (next) next.addEventListener('click', () => this.goTo(this.index + 1));

        this.thumbs.forEach((t) =>
          t.addEventListener('click', () => this.goTo(parseInt(t.dataset.index, 10)))
        );
        this.dots.forEach((d) =>
          d.addEventListener('click', () => this.goTo(parseInt(d.dataset.index, 10)))
        );

        this.bindDrag();
        this.bindVariant();
      }

      goTo(i) {
        this.index = this.clamp(i);
        this.render(true);
      }

      render(animate) {
        const h = this.stage.clientHeight;
        if (!animate) this.classList.add('is-dragging'); // suppress transition
        this.track.style.transform = 'translateY(' + -this.index * h + 'px)';
        if (!animate) {
          // force reflow then re-enable transition for the next interaction
          void this.track.offsetWidth;
          this.classList.remove('is-dragging');
        }

        this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
        this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === this.index));
        this.dots.forEach((d, i) => d.classList.toggle('is-active', i === this.index));

        const activeThumb = this.thumbs[this.index];
        if (activeThumb && activeThumb.scrollIntoView) {
          activeThumb.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }

      /* Vertical drag / swipe on the stage (mouse, touch, pen). */
      bindDrag() {
        const el = this.stage;
        let startY = 0;
        let startX = 0;
        let pointerId = null;
        let base = 0;
        let dragging = false;

        el.addEventListener('pointerdown', (e) => {
          if (e.button && e.button !== 0) return;
          // Don't hijack clicks on the arrows / thumbnails / zoom button — let them fire normally.
          if (e.target.closest('.lux-gallery__nav, .lux-gallery__thumb, .lux-gallery__dot, .lux-gallery__zoom')) return;
          pointerId = e.pointerId;
          startY = e.clientY;
          startX = e.clientX;
          base = -this.index * this.stage.clientHeight;
          dragging = false;
        });

        el.addEventListener('pointermove', (e) => {
          if (pointerId === null || e.pointerId !== pointerId) return;
          const dy = e.clientY - startY;
          const dx = e.clientX - startX;
          if (!dragging && Math.abs(dy) > 6 && Math.abs(dy) > Math.abs(dx)) {
            dragging = true;
            this.classList.add('is-dragging');
            try { el.setPointerCapture(pointerId); } catch (err) {}
          }
          if (!dragging) return;
          const h = this.stage.clientHeight;
          let offset = base + dy;
          const min = -(this.count - 1) * h;
          if (offset > 0) offset = offset * 0.3;
          else if (offset < min) offset = min + (offset - min) * 0.3;
          this.track.style.transform = 'translateY(' + offset + 'px)';
        });

        const end = (e) => {
          if (pointerId === null || (e.pointerId !== undefined && e.pointerId !== pointerId)) return;
          pointerId = null;
          if (!dragging) return;
          dragging = false;
          this.classList.remove('is-dragging');
          const dy = e.clientY - startY;
          const threshold = this.stage.clientHeight * 0.15;
          if (dy <= -threshold) this.goTo(this.index + 1);
          else if (dy >= threshold) this.goTo(this.index - 1);
          else this.render(true);
        };

        el.addEventListener('pointerup', end);
        el.addEventListener('pointercancel', end);
        el.addEventListener('pointerleave', end);
      }

      /* When a variant with its own image is selected, jump the gallery to it. */
      bindVariant() {
        const jumpToMedia = (mediaId) => {
          if (mediaId == null) return;
          const idx = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
          if (idx >= 0) this.goTo(idx);
        };
        this._variantHandler = (e) => {
          const v = e.detail && e.detail.variant;
          if (v && v.featured_media) jumpToMedia(v.featured_media.id);
        };
        this._variantInfoEl = this.closest('.product') || document;
        this._variantInfoEl.addEventListener('variant:change', this._variantHandler);
        document.addEventListener('on:variant:change', this._variantHandler);
      }

      /* ============================================================
         Fullscreen zoom viewer
         ============================================================ */
      buildZoom() {
        this.lightbox = this.querySelector('[data-lux-zoom]');
        if (!this.lightbox) return;
        // Relocate to <body> so position:fixed escapes any transformed/sticky ancestor.
        document.body.appendChild(this.lightbox);

        this.zViewport = this.lightbox.querySelector('.lux-zoom__viewport');
        this.zTrack = this.lightbox.querySelector('.lux-zoom__track');
        this.zSlides = Array.from(this.lightbox.querySelectorAll('.lux-zoom__slide'));
        this.zImgs = Array.from(this.lightbox.querySelectorAll('.lux-zoom__img'));
        this.zDots = Array.from(this.lightbox.querySelectorAll('.lux-zoom__dot'));
        this.zCount = this.zSlides.length;

        this.isOpen = false;
        this.zi = 0;
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this._pointers = new Map();

        this.bindZoomControls();
      }

      /* Open the viewer on a genuine tap on the stage image (independent of the
         vertical-swipe handler in bindDrag) or on the zoom button. */
      bindZoomOpen() {
        if (!this.lightbox || !this.stage) return;

        const btn = this.querySelector('.lux-gallery__zoom');
        if (btn) {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.openZoom(this.index);
          });
        }

        let sx = 0, sy = 0, pid = null, moved = false;
        this.stage.addEventListener('pointerdown', (e) => {
          if (e.target.closest('.lux-gallery__nav, .lux-gallery__thumb, .lux-gallery__dot, .lux-gallery__zoom')) return;
          pid = e.pointerId; sx = e.clientX; sy = e.clientY; moved = false;
        });
        this.stage.addEventListener('pointermove', (e) => {
          if (pid === null || e.pointerId !== pid) return;
          if (Math.hypot(e.clientX - sx, e.clientY - sy) > 10) moved = true;
        });
        const up = (e) => {
          if (pid === null || e.pointerId !== pid) return;
          const wasTap = !moved;
          pid = null;
          if (wasTap) this.openZoom(this.index);
        };
        this.stage.addEventListener('pointerup', up);
        this.stage.addEventListener('pointercancel', () => { pid = null; });
      }

      bindZoomControls() {
        const q = (s) => this.lightbox.querySelector(s);
        this._closeBtn = q('.lux-zoom__close');
        if (this._closeBtn) this._closeBtn.addEventListener('click', () => this.closeZoom());
        const prev = q('.lux-zoom__nav--prev');
        const next = q('.lux-zoom__nav--next');
        if (prev) prev.addEventListener('click', () => this.setIndex(this.zi - 1, true));
        if (next) next.addEventListener('click', () => this.setIndex(this.zi + 1, true));
        this.zDots.forEach((d) =>
          d.addEventListener('click', () => this.setIndex(parseInt(d.dataset.index, 10), true))
        );

        this.zViewport.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
        this.zViewport.addEventListener('pointerdown', (e) => this.onZPointerDown(e));
        this.zViewport.addEventListener('pointermove', (e) => this.onZPointerMove(e));
        const zend = (e) => this.onZPointerUp(e);
        this.zViewport.addEventListener('pointerup', zend);
        this.zViewport.addEventListener('pointercancel', zend);

        this._onKey = (e) => {
          if (!this.isOpen) return;
          if (e.key === 'Escape') this.closeZoom();
          else if (e.key === 'ArrowLeft') this.setIndex(this.zi - 1, true);
          else if (e.key === 'ArrowRight') this.setIndex(this.zi + 1, true);
        };
        document.addEventListener('keydown', this._onKey);
      }

      openZoom(i) {
        if (!this.lightbox) return;
        this.isOpen = true;
        this.lightbox.hidden = false;
        void this.lightbox.offsetWidth; // reflow so the opacity transition runs
        this.lightbox.classList.add('is-open');
        this.lightbox.setAttribute('aria-hidden', 'false');
        document.body.classList.add('lux-zoom-open');
        // Eagerly load the current image + its neighbours (lazy imgs in a hidden layer don't fetch).
        [i - 1, i, i + 1].forEach((n) => { const im = this.zImgs[n]; if (im) im.loading = 'eager'; });
        this.setIndex(this.clampZ(i), false);
        if (this._closeBtn) this._closeBtn.focus({ preventScroll: true });
      }

      closeZoom() {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.resetZoom();
        this.lightbox.classList.remove('is-open');
        this.lightbox.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('lux-zoom-open');
        // Keep the main gallery in sync with whatever image we ended on.
        if (this.count > 1) this.goTo(this.zi);
        const done = () => { if (!this.isOpen) this.lightbox.hidden = true; this.lightbox.removeEventListener('transitionend', done); };
        this.lightbox.addEventListener('transitionend', done);
        setTimeout(() => { if (!this.isOpen) this.lightbox.hidden = true; }, 400);
      }

      clampZ(i) {
        return Math.max(0, Math.min(this.zCount - 1, i));
      }

      viewportW() {
        return this.zViewport.clientWidth;
      }

      activeImg() {
        return this.zImgs[this.zi];
      }

      setIndex(i, animate) {
        i = this.clampZ(i);
        this.zi = i;
        this.resetZoom();
        this.zTrack.style.transition = animate ? '' : 'none';
        this.zTrack.style.transform = 'translateX(' + -i * 100 + '%)';
        if (!animate) { void this.zTrack.offsetWidth; this.zTrack.style.transition = ''; }
        this.zDots.forEach((d, idx) => d.classList.toggle('is-active', idx === i));
        [i - 1, i, i + 1].forEach((n) => { const im = this.zImgs[n]; if (im) im.loading = 'eager'; });
      }

      resetZoom() {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.applyTransform();
      }

      applyTransform() {
        const im = this.activeImg();
        if (im) im.style.transform = 'translate(' + this.tx + 'px,' + this.ty + 'px) scale(' + this.scale + ')';
        this.lightbox.classList.toggle('is-zoomed', this.scale > 1.01);
      }

      /* Zoom toward a screen focal point, keeping that point stationary. */
      zoomAt(newScale, fx, fy) {
        newScale = Math.max(1, Math.min(4, newScale));
        const im = this.activeImg();
        if (!im) return;
        const r = im.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const k = newScale / this.scale;
        this.tx -= (fx - cx) * (k - 1);
        this.ty -= (fy - cy) * (k - 1);
        this.scale = newScale;
        if (this.scale <= 1.001) { this.tx = 0; this.ty = 0; }
        this.clampPan();
        this.applyTransform();
      }

      clampPan() {
        const im = this.activeImg();
        if (!im) return;
        const vpW = this.zViewport.clientWidth;
        const vpH = this.zViewport.clientHeight;
        const w = im.clientWidth * this.scale;
        const h = im.clientHeight * this.scale;
        const maxX = Math.max(0, (w - vpW) / 2);
        const maxY = Math.max(0, (h - vpH) / 2);
        this.tx = Math.max(-maxX, Math.min(maxX, this.tx));
        this.ty = Math.max(-maxY, Math.min(maxY, this.ty));
      }

      onWheel(e) {
        e.preventDefault();
        const factor = e.deltaY < 0 ? 1.18 : 1 / 1.18;
        this.zoomAt(this.scale * factor, e.clientX, e.clientY);
      }

      onZPointerDown(e) {
        if (e.target.closest('.lux-zoom__close, .lux-zoom__nav, .lux-zoom__dot')) return;
        this._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        try { this.zViewport.setPointerCapture(e.pointerId); } catch (err) {}

        if (this._pointers.size === 2) {
          const pts = [...this._pointers.values()];
          this._pinchDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
          this._pinchScale = this.scale;
          this._swipe = null;
          this._pan = null;
        } else if (this._pointers.size === 1) {
          this._downX = e.clientX;
          this._downY = e.clientY;
          this._moved = false;
          if (this.scale > 1) {
            this._pan = { x: e.clientX, y: e.clientY };
            this._swipe = null;
          } else {
            this._swipe = { x: e.clientX, y: e.clientY, base: -this.zi * this.viewportW(), dx: 0, dy: 0 };
            this._pan = null;
          }
        }
      }

      onZPointerMove(e) {
        const p = this._pointers.get(e.pointerId);
        if (!p) return;
        p.x = e.clientX;
        p.y = e.clientY;

        if (this._pointers.size >= 2 && this._pinchDist) {
          const pts = [...this._pointers.values()];
          const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
          const midX = (pts[0].x + pts[1].x) / 2;
          const midY = (pts[0].y + pts[1].y) / 2;
          this.zoomAt(this._pinchScale * (dist / this._pinchDist), midX, midY);
          return;
        }

        if (this._pan) {
          this.tx += e.clientX - this._pan.x;
          this.ty += e.clientY - this._pan.y;
          this._pan.x = e.clientX;
          this._pan.y = e.clientY;
          this._moved = true;
          this.clampPan();
          this.applyTransform();
          return;
        }

        if (this._swipe) {
          const dx = e.clientX - this._swipe.x;
          const dy = e.clientY - this._swipe.y;
          this._swipe.dx = dx;
          this._swipe.dy = dy;
          if (Math.abs(dx) > 8 || Math.abs(dy) > 8) this._moved = true;
          // Follow horizontally; a mostly-vertical drag is a dismiss gesture (don't slide the track).
          if (Math.abs(dx) > Math.abs(dy)) {
            this.zTrack.style.transition = 'none';
            this.zTrack.style.transform = 'translateX(' + (this._swipe.base + dx) + 'px)';
          }
        }
      }

      onZPointerUp(e) {
        if (!this._pointers.has(e.pointerId)) return;
        this._pointers.delete(e.pointerId);

        // Dropping from two fingers to one: continue as a pan if still zoomed.
        if (this._pointers.size === 1 && this._pinchDist) {
          this._pinchDist = null;
          const pt = [...this._pointers.values()][0];
          this._pan = this.scale > 1 ? { x: pt.x, y: pt.y } : null;
          this._swipe = null;
          return;
        }

        if (this._pointers.size > 0) return;

        if (this._swipe) {
          const dx = this._swipe.dx;
          const dy = this._swipe.dy;
          this._swipe = null;
          if (!this._moved) {
            // A tap: toggle zoom on the image, or close when tapping the letterbox area.
            if (e.target.closest('.lux-zoom__img')) this.toggleZoomAt(e.clientX, e.clientY);
            else this.closeZoom();
          } else if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > this.viewportW() * 0.15) {
            this.setIndex(this.zi + (dx < 0 ? 1 : -1), true);
          } else if (dy > 90 && Math.abs(dy) > Math.abs(dx)) {
            this.closeZoom(); // swipe down to dismiss
          } else {
            this.setIndex(this.zi, true); // snap back
          }
        } else if (this._pan) {
          this._pan = null;
        } else {
          this._pinchDist = null;
          if (this.scale <= 1.001) this.resetZoom();
        }
      }

      toggleZoomAt(x, y) {
        if (this.scale > 1.01) this.resetZoom();
        else this.zoomAt(2.5, x, y);
      }
    }
  );
}
